import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { Plus, Upload } from 'lucide-react';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExcelUploadDialog,
  Page,
  PageContent,
  UnsavedChangesConfirmDialog
} from '@bliks-io/ui';
import { useAuthStore } from '@/stores/auth-store';
import { toastApiError } from '@/api/mutations/toast-api-error';
import {
  useInitializeValidateDepartments,
  useCreateDepartmentsBulk
} from '@/api/mutations/useDepartments';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePageUnsavedChanges } from '@/hooks/usePageUnsavedChanges';
import { translateErrorMessage } from '@/lib/errors';
import type { ApiError } from '@/lib/agent.types';
import { CrudErrorAlert } from '@/components/crud/CrudErrorAlert';
import type {
  BulkValidateDepartmentInput,
  InitializeValidateDepartmentsResponse
} from '@bliks-io/contracts';
import { DepartmentDraftTable, type DraftDepartment } from './components/DepartmentDraftTable';
import { ManualAddDialog } from './components/ManualAddDialog';
import { parseDepartmentsFromExcel } from './utils/excelParser';
import { downloadDepartmentTemplate } from './utils/excelTemplate';

export function CreateDepartmentsBulkPage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.createDepartments');
  const navigate = useNavigate();
  const slug = useAuthStore().slug();

  const validateMutation = useInitializeValidateDepartments();
  const createMutation = useCreateDepartmentsBulk();

  const [drafts, setDrafts] = useState<DraftDepartment[]>([]);
  const [validationResponse, setValidationResponse] =
    useState<InitializeValidateDepartmentsResponse | null>(null);
  const [batchValid, setBatchValid] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingDraft, setEditingDraft] = useState<DraftDepartment | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isManualFormOpen, setIsManualFormOpen] = useState(false);
  const [isExcelUploadOpen, setIsExcelUploadOpen] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Ref to track when we're saving (to prevent blocking navigation on successful save)
  const isSavingRef = useRef(false);

  // Unsaved changes handling
  const { showConfirmation, handleNavigateWithConfirmation, handleStay, handleDiscard } =
    usePageUnsavedChanges({
      hasUnsavedChanges: drafts.length > 0 && !isSavingRef.current,
      onNavigateAway: () => navigate({ to: '/$slug/organization/departments', params: { slug } })
    });

  /**
   * Validate departments with backend
   */
  const validateDepartments = async (departments: BulkValidateDepartmentInput[]) => {
    try {
      const result = await validateMutation.mutateAsync({ departments });
      setValidationResponse(result);
      setBatchValid(result.batchValid);

      // Convert validated departments to drafts with UUIDs
      const newDrafts: DraftDepartment[] = result.departments.map((vd) => ({
        ...vd,
        id: uuidv4()
      }));

      setDrafts(newDrafts);
      return result;
    } catch (error) {
      console.error('Validation error:', error);
      const apiError = error as ApiError;
      const errorMessage = translateErrorMessage(apiError, t);
      setError(errorMessage);
      toastApiError(error, 'organization.departments.bulkUploadPage.validationError');
      return null;
    }
  };

  const handleDownloadTemplate = () => {
    downloadDepartmentTemplate();
    toast.success(t('organization.departments.bulkUploadPage.downloadTemplate'));
  };

  /**
   * Parse an uploaded Excel file, validate the rows with the backend, and report
   * the local parse outcome. The shared dialog closes on resolve; throwing keeps
   * it open (matching the previous empty-file behavior).
   */
  const handleExcelUpload = async (file: File): Promise<number> => {
    const result = await parseDepartmentsFromExcel(file);

    if (result.totalRows === 0) {
      throw new Error(t('organization.departments.bulkUploadPage.uploadError'));
    }

    await validateDepartments(result.departments.map((d) => d.data));

    if (result.invalidRows > 0 && result.validRows > 0) {
      toast.warning(
        t('organization.departments.bulkUploadPage.uploadPartial', {
          valid: result.validRows,
          invalid: result.invalidRows
        })
      );
    } else if (result.invalidRows > 0) {
      toast.error(
        t('organization.departments.bulkUploadPage.uploadAllInvalid', {
          count: result.invalidRows
        })
      );
    } else {
      toast.success(
        t('organization.departments.bulkUploadPage.uploadSuccess', {
          count: result.validRows
        })
      );
    }

    return result.validRows;
  };

  /**
   * Add department manually
   */
  const handleAddDepartment = async (department: BulkValidateDepartmentInput) => {
    const allDepartments = [...drafts.map((d) => d.data), department];
    return (await validateDepartments(allDepartments)) !== null;
  };

  /**
   * Edit draft
   */
  const handleEditDraft = (draft: DraftDepartment) => {
    setEditingDraft(draft);
  };

  const handleSaveEdit = async (updatedData: BulkValidateDepartmentInput) => {
    if (!editingDraft) return false;

    // Update the draft data
    const updatedDrafts = drafts.map((d) =>
      d.id === editingDraft.id ? { ...d, data: updatedData } : d
    );

    // Re-validate entire batch with backend
    const allDepartments = updatedDrafts.map((d) => d.data);
    const result = await validateDepartments(allDepartments);
    if (!result) return false;

    // Merge validation results back into drafts (preserving UUIDs)
    const mergedDrafts = result.departments.map((vd, index) => ({
      ...vd,
      id: updatedDrafts[index].id // Keep original UUID
    }));
    setDrafts(mergedDrafts);
    return true;
  };

  /**
   * Delete draft
   */
  const handleDeleteDraft = (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;

    const remainingDrafts = drafts.filter((d) => d.id !== deleteConfirmId);

    if (remainingDrafts.length === 0) {
      // If no drafts remaining, just clear everything
      setDrafts([]);
      setValidationResponse(null);
      setBatchValid(false);
    } else {
      // Re-validate remaining departments
      const allDepartments = remainingDrafts.map((d) => d.data);
      await validateDepartments(allDepartments);
    }

    setDeleteConfirmId(null);
  };

  /**
   * Create all departments (all-or-nothing)
   */
  const handleCreateAll = async () => {
    if (!batchValid || !validationResponse) {
      toast.error(t('organization.departments.bulkUploadPage.fixErrorsWarning'));
      return;
    }

    setIsCreating(true);
    isSavingRef.current = true;
    setError(undefined);

    try {
      // Submit all validated departments to backend
      // Map validation response fields to match initialize contract schema
      await createMutation.mutateAsync({
        departments: validationResponse.departments.map((d) => ({
          name: d.data.name,
          description: d.data.description,
          abbreviation: d.data.abbreviation,
          parentAbbr: d.data.parentAbbreviation,
          managerEmail: d.data.managerEmail!
        }))
      });

      toast.success(
        t('organization.departments.bulkUploadPage.successCreated', {
          count: drafts.length
        })
      );

      // Navigate to departments list on success
      navigate({ to: '/$slug/organization/departments', params: { slug } });
    } catch (error) {
      console.error('Error creating departments:', error);
      isSavingRef.current = false; // Reset flag on error
      const apiError = error as ApiError;
      const errorMessage = translateErrorMessage(apiError, t);
      setError(errorMessage);
      toastApiError(error, 'organization.departments.bulkUploadPage.validationError');
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Cancel and go back
   */
  const handleCancel = () => {
    handleNavigateWithConfirmation();
  };

  // Get existing abbreviations for the parent combo box
  const existingAbbreviations = drafts.map((d) => d.data.abbreviation);

  return (
    <Page>
      <PageContent>
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold leading-7">
              {t('organization.departments.bulkUploadPage.title')}
            </h1>
            <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
              {t('organization.departments.bulkUploadPage.description')}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button variant="outline" size="medium" onClick={() => setIsExcelUploadOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              {t('organization.departments.bulkUploadPage.uploadFromExcel')}
            </Button>
            <Button variant="primary" size="medium" onClick={() => setIsManualFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('organization.departments.bulkUploadPage.addManually')}
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        <CrudErrorAlert error={error} onDismiss={() => setError(undefined)} className="mb-6" />

        {/* Circular Dependency Warning */}
        {validationResponse && !batchValid && drafts.every((d) => d.isValid) && (
          <div className="mb-6 p-4 border border-warning-solid bg-warning-surface rounded-lg">
            <p className="text-sm text-warning-strong font-semibold">
              ⚠️ {t('organization.departments.bulkUploadPage.circularDependency')}
            </p>
            <p className="text-xs text-warning mt-1">
              {t('organization.departments.bulkUploadPage.circularDependencyReview')}
            </p>
          </div>
        )}

        {/* Draft Departments Table */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">
            {t('organization.departments.bulkUploadPage.draftDepartments')} ({drafts.length})
          </h2>
          <DepartmentDraftTable
            drafts={drafts}
            onEdit={handleEditDraft}
            onDelete={handleDeleteDraft}
          />
        </div>

        {/* Bottom Actions */}
        <div className="flex items-center justify-end gap-4 pt-4 border-t">
          <Button variant="outline" size="medium" onClick={handleCancel}>
            {t('organization.departments.bulkUploadPage.cancel')}
          </Button>
          <div className="flex flex-col items-end gap-2">
            {!batchValid && drafts.length > 0 && (
              <p className="text-sm text-warning">
                {t('organization.departments.bulkUploadPage.fixErrorsWarning')}
              </p>
            )}
            <Button
              variant="primary"
              size="medium"
              onClick={handleCreateAll}
              disabled={drafts.length === 0 || !batchValid || isCreating}
              loading={isCreating}
            >
              <ENTITY_ICONS.department className="h-4 w-4 mr-2" />
              {t('organization.departments.bulkUploadPage.createAll')} ({drafts.length})
            </Button>
          </div>
        </div>

        {/* Edit Dialog */}
        {editingDraft && (
          <ManualAddDialog
            open={!!editingDraft}
            onOpenChange={() => setEditingDraft(null)}
            onAdd={handleSaveEdit}
            initialData={editingDraft.data}
            existingAbbreviations={existingAbbreviations}
            isEditing
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={!!deleteConfirmId}
          onOpenChange={() => {
            if (!validateMutation.isPending) setDeleteConfirmId(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
              <DialogDescription>
                {t('organization.departments.bulkUploadPage.confirmDeleteDraft')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmId(null)}
                disabled={validateMutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                loading={validateMutation.isPending}
              >
                {t('common.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manual Form Dialog */}
        <ManualAddDialog
          open={isManualFormOpen}
          onOpenChange={setIsManualFormOpen}
          onAdd={handleAddDepartment}
          existingAbbreviations={existingAbbreviations}
        />

        {/* Excel Upload Dialog */}
        <ExcelUploadDialog
          open={isExcelUploadOpen}
          onOpenChange={setIsExcelUploadOpen}
          title={t('organization.departments.bulkUploadPage.uploadFromExcel')}
          description={t('organization.departments.bulkUploadPage.description')}
          downloadTemplate={handleDownloadTemplate}
          parse={handleExcelUpload}
          errorToast={t('organization.departments.bulkUploadPage.uploadError')}
          showSuccessToast={false}
        />

        {/* Unsaved Changes Confirmation Dialog */}
        <UnsavedChangesConfirmDialog
          open={showConfirmation}
          onStay={handleStay}
          onDiscard={handleDiscard}
        />
      </PageContent>
    </Page>
  );
}
